import classNames from 'classnames';
import React, { FC, useEffect, useRef, useMemo, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import LogModule from './Logs/LogModule';
import { inspectorSelectors } from 'redux/app/vessel/inspector';
import { IStore } from 'redux/store';
import LogModuleBack from './Logs/LogModuleBack';

const defaultWindow = window as Window;
const defaultDocument = window.document as HTMLDocument;

// Individual Template Component with its own execution context
const IsolatedTemplate: React.FC<{
    template: any;
    index: number;
    onTemplateRef: (index: number, el: HTMLDivElement | null) => void;
}> = ({ template, index, onTemplateRef }) => {
    const templateRef = useRef<HTMLDivElement>(null);
    const scriptExecutedRef = useRef(false);
    const cleanupRef = useRef<(() => void) | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const messageHandlerRef =
        useRef<((event: MessageEvent) => void) | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup function
    const cleanup = useCallback(() => {
        // Clear timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Remove message handler
        if (messageHandlerRef.current) {
            window.removeEventListener('message', messageHandlerRef.current);
            messageHandlerRef.current = null;
        }

        // Remove iframe
        if (iframeRef.current && document.body.contains(iframeRef.current)) {
            try {
                document.body.removeChild(iframeRef.current);
            } catch (error) {
                console.warn('Error removing iframe:', error);
            }
            iframeRef.current = null;
        }

        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
    }, []);

    useEffect(() => {
        // Don't cleanup immediately, only if template actually changes
        const currentTemplateId = template?.id;
        const previousTemplateId = templateRef.current?.getAttribute(
            'data-current-template-id'
        );

        if (previousTemplateId && previousTemplateId !== currentTemplateId) {
            cleanup();
            scriptExecutedRef.current = false;
        }

        if (
            templateRef.current &&
            !scriptExecutedRef.current &&
            template.html &&
            template.js
        ) {
            const container = templateRef.current;
            const contentDiv = container.querySelector('.template-content');

            if (!contentDiv) return;

            // Set current template ID
            container.setAttribute(
                'data-current-template-id',
                currentTemplateId
            );

            // Create a completely isolated script execution
            timeoutRef.current = setTimeout(() => {
                try {
                    // Create a temporary iframe for script execution
                    const iframe = document.createElement('iframe');
                    iframe.style.display = 'none';
                    iframe.style.position = 'absolute';
                    iframe.style.top = '-9999px';
                    iframe.style.left = '-9999px';
                    iframe.setAttribute(
                        'sandbox',
                        'allow-scripts allow-same-origin'
                    );
                    iframe.id = `template-iframe-${index}-${Date.now()}`;
                    document.body.appendChild(iframe);
                    iframeRef.current = iframe;

                    const iframeDoc =
                        iframe.contentDocument ||
                        iframe.contentWindow?.document;
                    if (!iframeDoc) {
                        cleanup();
                        return;
                    }

                    // Set up the iframe with the template
                    iframeDoc.open();
                    iframeDoc.write(`
                        <html>
                            <head>
                                <style>
                                    body { margin: 0; padding: 0; font-family: inherit; }
                                    * { box-sizing: border-box; }
                                </style>
                            </head>
                            <body>
                                ${template.html}
                                <script>
                                    var templateData = ${JSON.stringify(
                                        template.data
                                    )};
                                    var templateConfig = ${JSON.stringify(
                                        template.config || {}
                                    )};
                                    var templateId = '${template.id}';
                                    var templateIndex = ${index};
                                    
                                    try {
                                        ${template.js}
                                        
                                        // Execute getData
                                        setTimeout(function() {
                                            if (typeof getData === 'function') {
                                                try {
                                                    if (templateConfig && Object.keys(templateConfig).length > 0) {
                                                        getData([templateData], templateConfig);
                                                    } else {
                                                        getData([templateData]);
                                                    }
                                                    
                                                    // Copy the rendered content back to the main document
                                                    setTimeout(function() {
                                                        parent.postMessage({
                                                            type: 'templateRendered',
                                                            index: templateIndex,
                                                            content: document.body.innerHTML,
                                                            templateId: templateId,
                                                            timestamp: Date.now()
                                                        }, '*');
                                                    }, 100);
                                                } catch (renderError) {
                                                    console.error('getData execution error:', renderError);
                                                    parent.postMessage({
                                                        type: 'templateError',
                                                        index: templateIndex,
                                                        error: 'getData execution failed: ' + renderError.message,
                                                        templateId: templateId
                                                    }, '*');
                                                }
                                            } else {
                                                parent.postMessage({
                                                    type: 'templateError',
                                                    index: templateIndex,
                                                    error: 'getData function not found',
                                                    templateId: templateId
                                                }, '*');
                                            }
                                        }, 50);
                                    } catch (error) {
                                        console.error('Template execution error:', error);
                                        parent.postMessage({
                                            type: 'templateError',
                                            index: templateIndex,
                                            error: error.message,
                                            templateId: templateId
                                        }, '*');
                                    }
                                </script>
                            </body>
                        </html>
                    `);
                    iframeDoc.close();

                    // Listen for the rendered content
                    const messageHandler = (event: MessageEvent) => {
                        if (
                            event.data.type === 'templateRendered' &&
                            event.data.index === index &&
                            event.data.templateId === template.id
                        ) {
                            if (contentDiv && templateRef.current) {
                                // Clear loading message first
                                contentDiv.innerHTML = '';
                                // Add rendered content
                                contentDiv.innerHTML = event.data.content;
                            }
                            scriptExecutedRef.current = true;
                            // Don't cleanup immediately, let the content render
                            setTimeout(cleanup, 1000);
                        } else if (
                            event.data.type === 'templateError' &&
                            event.data.index === index &&
                            event.data.templateId === template.id
                        ) {
                            console.error(
                                `Template ${index} error:`,
                                event.data.error
                            );
                            if (contentDiv && templateRef.current) {
                                contentDiv.innerHTML = `
                                    <div class="text-red-500 text-sm p-4 border border-red-300 rounded">
                                        <strong>Template Error:</strong><br/>
                                        ${event.data.error}
                                    </div>
                                `;
                            }
                            setTimeout(cleanup, 500);
                        }
                    };

                    window.addEventListener('message', messageHandler);
                    messageHandlerRef.current = messageHandler;

                    // Set cleanup function
                    cleanupRef.current = () => {
                        if (messageHandlerRef.current) {
                            window.removeEventListener(
                                'message',
                                messageHandlerRef.current
                            );
                            messageHandlerRef.current = null;
                        }
                        if (
                            iframeRef.current &&
                            document.body.contains(iframeRef.current)
                        ) {
                            try {
                                document.body.removeChild(iframeRef.current);
                            } catch (error) {
                                console.warn(
                                    'Error removing iframe in cleanup:',
                                    error
                                );
                            }
                            iframeRef.current = null;
                        }
                    };

                    // Fallback cleanup timeout
                    setTimeout(() => {
                        if (!scriptExecutedRef.current) {
                            if (contentDiv && templateRef.current) {
                                contentDiv.innerHTML = `
                                    <div class="text-yellow-600 text-sm p-4 border border-yellow-300 rounded">
                                        Template loading timed out. Please try refreshing.
                                    </div>
                                `;
                            }
                            cleanup();
                        }
                    }, 15000); // Increased timeout to 15 seconds
                } catch (error) {
                    console.error(`Error setting up template ${index}:`, error);
                    cleanup();
                }
            }, Math.max(index * 100, 100)); // Reduced stagger time but ensure minimum delay

            // Return cleanup function for timeout
            return () => {
                cleanup();
            };
        }

        return () => {
            // Only cleanup on unmount or significant changes
        };
    }, [template, index, cleanup]);

    useEffect(() => {
        onTemplateRef(index, templateRef.current);
    }, [index, onTemplateRef]);

    // Cleanup on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    return (
        <div
            ref={templateRef}
            id={`template-container-${index}`}
            className="template-wrapper bg-white overflow-hidden"
            data-template-id={template.id}
            data-template-index={index}
            style={{
                isolation: 'isolate',
                minHeight: '300px',
                pageBreakAfter: 'always',
                marginBottom: '2rem',
            }}
        >
            {/* Template Content */}
            <div className="template-content" style={{ minHeight: '250px' }}>
                <div className="text-sm text-gray-500 mb-2 p-4">
                    <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading template {index + 1}...</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

type TTemplateRendererProps = PropsFromRedux & {
    zoomInRef: React.MutableRefObject<any>;
    zoomOutRef: React.MutableRefObject<any>;
    setDefaultStateRef: React.MutableRefObject<any>;
    transformComponentRef: React.MutableRefObject<any>;
    isDrag: boolean;
    isFullScreen: boolean;
    document?: HTMLDocument;
    window?: Window;
    isTableWidthFull?: boolean;
};

const TemplateRenderer: FC<TTemplateRendererProps> = (props) => {
    const {
        logTemplate,
        logConfig,
        logDataArray,
        logData,
        zoomInRef,
        zoomOutRef,
        setDefaultStateRef,
        transformComponentRef,
        logType,
        isDrag,
        isFullScreen,
        entry,
        isTableWidthFull = false,
        showCoverPages,
    } = props;

    // Always call these hooks at the top level
    const templateContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const scriptCleanupRef = useRef<(() => void) | null>(null);
    const previousLogTypeRef = useRef<string | null>(null);
    const previousEntryIdRef = useRef<string | null>(null);
    const previousTemplateIdRef = useRef<string | null>(null);

    // Always compute finalData
    const finalData = useMemo(() => {
        return logDataArray && logDataArray?.length > 0
            ? logDataArray
            : logData;
    }, [logDataArray, logData]);

    // Helper function - always defined
    const b64_to_utf8 = useCallback((data: string) => {
        try {
            return decodeURIComponent(escape(window.atob(data)));
        } catch (error) {
            console.error('Error decoding base64:', error);
            return '';
        }
    }, []);

    // Comprehensive cleanup function
    const performFullCleanup = useCallback(() => {
        console.log('Performing full cleanup...');

        // Clean up previous scripts
        if (scriptCleanupRef.current) {
            try {
                scriptCleanupRef.current();
            } catch (error) {
                console.warn('Error during script cleanup:', error);
            }
            scriptCleanupRef.current = null;
        }

        // Remove all print scripts
        const printScripts = document.getElementsByClassName('printscript');
        const scriptsToRemove = Array.from(printScripts);
        scriptsToRemove.forEach((script) => {
            try {
                if (script && script.parentNode) {
                    script.parentNode.removeChild(script);
                }
            } catch (error) {
                console.warn('Error removing script:', error);
            }
        });

        // Clear any global getData function safely
        try {
            if ((window as any).getData) {
                (window as any).getData = undefined;
            }
            // Also clear our namespaced version if it exists
            if ((window as any).templateRenderer) {
                (window as any).templateRenderer = undefined;
            }
        } catch (error) {
            console.warn('Could not clear global functions:', error);
        }

        // Reset template container refs
        templateContainerRefs.current = [];

        console.log('Full cleanup completed');
    }, []);

    // Always compute template data for permit logs
    const templateData = useMemo(() => {
        if (logType !== 'permitLogs' || !finalData || !logTemplate) return [];

        const dataArray = Array.isArray(finalData) ? finalData : [finalData];

        return dataArray
            .map((dataItem, index) => {
                try {
                    const templateId = `${
                        dataItem._id || dataItem.id || 'template'
                    }-${index}-${Date.now()}`;
                    return {
                        id: templateId,
                        index,
                        html: logTemplate.html
                            ? b64_to_utf8(logTemplate.html)
                            : '',
                        js: logTemplate.js ? b64_to_utf8(logTemplate.js) : '',
                        data: dataItem,
                        config: logConfig,
                    };
                } catch (error) {
                    console.error(`Error processing template ${index}:`, error);
                    return null;
                }
            })
            .filter(Boolean);
    }, [logType, finalData, logTemplate, logConfig, b64_to_utf8]);

    // Always compute regular template data
    const { dynamicJsScript, htmlcontent } = useMemo(() => {
        let dynamicJsScript: any;
        let htmlcontent: any;

        if (logType !== 'permitLogs') {
            try {
                if (logTemplate && logTemplate.js) {
                    dynamicJsScript = b64_to_utf8(logTemplate.js);
                }

                if (logTemplate && logTemplate.html) {
                    htmlcontent = b64_to_utf8(logTemplate.html);
                }
            } catch (error) {
                console.log('Error processing regular template:', error);
            }
        }

        return { dynamicJsScript, htmlcontent };
    }, [logType, logTemplate, b64_to_utf8]);

    // Always set up window error handler
    useEffect(() => {
        const errorHandler = (error: any) => {
            console.error('Print Template Window Error:', error);
        };

        window.onerror = errorHandler;

        return () => {
            window.onerror = null;
        };
    }, []);

    // Cleanup when significant changes occur
    useEffect(() => {
        const currentEntryId = entry?.id || null;
        const currentTemplateId = logTemplate?.id || null;

        const shouldCleanup =
            (previousLogTypeRef.current !== null &&
                previousLogTypeRef.current !== logType) ||
            (previousEntryIdRef.current !== null &&
                previousEntryIdRef.current !== currentEntryId) ||
            (previousTemplateIdRef.current !== null &&
                previousTemplateIdRef.current !== currentTemplateId);

        if (shouldCleanup) {
            console.log('Significant change detected, cleaning up...', {
                logTypeChanged: previousLogTypeRef.current !== logType,
                entryChanged: previousEntryIdRef.current !== currentEntryId,
                templateChanged:
                    previousTemplateIdRef.current !== currentTemplateId,
            });
            performFullCleanup();
        }

        previousLogTypeRef.current = logType;
        previousEntryIdRef.current = currentEntryId;
        previousTemplateIdRef.current = currentTemplateId;
    }, [logType, entry?.id, logTemplate?.id, performFullCleanup]);

    // Always set up the script injection effect (but only run for non-permit logs)
    useEffect(() => {
        if (logType === 'permitLogs') return;

        // Clean up previous script first
        if (scriptCleanupRef.current) {
            try {
                scriptCleanupRef.current();
            } catch (error) {
                console.warn('Error during previous script cleanup:', error);
            }
        }

        if (
            logTemplate &&
            logTemplate.js &&
            logTemplate.html &&
            dynamicJsScript
        ) {
            console.log('Setting up regular template script...');

            const script = document.createElement('script');
            script.innerHTML = dynamicJsScript;
            script.classList.add('printscript');
            script.setAttribute(
                'data-template-id',
                `${logTemplate.id || 'unknown'}-${Date.now()}`
            );

            try {
                document.body.appendChild(script);

                // Create a safer namespace for our functions
                if (!(window as any).templateRenderer) {
                    (window as any).templateRenderer = {};
                }

                // Wait a bit for script to be processed
                const timeoutId = setTimeout(() => {
                    if ((window as any).getData) {
                        try {
                            if (logConfig) {
                                (window as any).getData(finalData, logConfig);
                            } else {
                                console.log(
                                    'No log config found, calling getData without config'
                                );
                                (window as any).getData(finalData);
                            }
                        } catch (error) {
                            console.error('Error calling getData:', error);
                        }
                    } else {
                        console.warn(
                            'getData function not found after script injection'
                        );
                    }
                }, 200); // Increased delay

                // Set cleanup function
                scriptCleanupRef.current = () => {
                    clearTimeout(timeoutId);
                    if (script && script.parentNode) {
                        try {
                            script.parentNode.removeChild(script);
                        } catch (error) {
                            console.warn('Error removing script:', error);
                        }
                    }
                    try {
                        if ((window as any).getData) {
                            (window as any).getData = undefined;
                        }
                    } catch (error) {
                        console.warn(
                            'Could not clear getData function:',
                            error
                        );
                    }
                };
            } catch (err) {
                console.error('Error inserting print template script:', err);
            }
        }

        return () => {
            if (scriptCleanupRef.current) {
                try {
                    scriptCleanupRef.current();
                } catch (error) {
                    console.warn(
                        'Error during script cleanup on unmount:',
                        error
                    );
                }
                scriptCleanupRef.current = null;
            }
        };
    }, [logTemplate, logType, finalData, dynamicJsScript, logConfig]);

    // Always define the template ref handler
    const handleTemplateRef = useCallback(
        (index: number, el: HTMLDivElement | null) => {
            templateContainerRefs.current[index] = el;
        },
        []
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            console.log('TemplateRenderer unmounting, performing cleanup...');
            performFullCleanup();
        };
    }, [performFullCleanup]);

    console.log({
        logType,
        entryId: entry?.id,
        templateId: logTemplate?.id,
        hasTemplateData: templateData.length > 0,
        hasHtmlContent: !!htmlcontent,
    });

    return (
        <div>
            <TransformWrapper
                wheel={{ wheelEnabled: false, touchPadEnabled: false }}
                options={{
                    limitToBounds: false,
                    centerContent: true,
                }}
                pan={{
                    disabled: !isDrag,
                }}
            >
                {({
                    zoomIn,
                    zoomOut,
                    resetTransform,
                    setDefaultState,
                    setTransform,
                    ...rest
                }: any) => {
                    zoomInRef.current = zoomIn;
                    zoomOutRef.current = zoomOut;
                    setDefaultStateRef.current = setDefaultState;

                    return (
                        <>
                            <div
                                className={classNames(
                                    'relative max-h-full px-0 mx-auto bg-white rounded-lg'
                                )}
                            >
                                <div
                                    id="transformElementWrapper"
                                    className={classNames(
                                        'relative overflow-hidden my-0 h-screen',
                                        {
                                            'h-auto': isTableWidthFull,
                                        }
                                    )}
                                >
                                    <TransformComponent>
                                        <div
                                            className={classNames('web-only', {
                                                'h-[calc(100vh-150px)] lg:w-[calc(100vw-430px)] w-full':
                                                    !isFullScreen &&
                                                    !isTableWidthFull,
                                                'w-full':
                                                    !isFullScreen &&
                                                    isTableWidthFull,
                                                'overflow-scroll cursor-default':
                                                    !isDrag,
                                                'cursor-move': isDrag,
                                                'w-screen h-screen':
                                                    isFullScreen,
                                            })}
                                        >
                                            <div ref={transformComponentRef}>
                                                {entry && showCoverPages ? (
                                                    <LogModule
                                                        bookId={entry?.id}
                                                    />
                                                ) : null}

                                                {/* Conditional rendering based on logType */}
                                                {logType === 'permitLogs' ? (
                                                    <>
                                                        {/* Render multiple templates with complete isolation */}
                                                        <div className="templates-container space-y-8">
                                                            {templateData.map(
                                                                (
                                                                    template,
                                                                    index
                                                                ) =>
                                                                    template ? (
                                                                        <IsolatedTemplate
                                                                            key={`${
                                                                                template.id
                                                                            }-${
                                                                                entry?.id ||
                                                                                'no-entry'
                                                                            }`}
                                                                            template={
                                                                                template
                                                                            }
                                                                            index={
                                                                                index
                                                                            }
                                                                            onTemplateRef={
                                                                                handleTemplateRef
                                                                            }
                                                                        />
                                                                    ) : null
                                                            )}
                                                        </div>

                                                        {/* No data message */}
                                                        {templateData.length ===
                                                            0 && (
                                                            <div className="flex justify-center items-center py-8">
                                                                <div className="text-sm text-gray-500">
                                                                    No template
                                                                    data
                                                                    available
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* Regular template rendering */}
                                                        {htmlcontent ? (
                                                            <div
                                                                key={`regular-template-${
                                                                    entry?.id ||
                                                                    'no-entry'
                                                                }-${
                                                                    logTemplate?.id ||
                                                                    'no-template'
                                                                }`}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: htmlcontent,
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="flex justify-center items-center py-8">
                                                                <div className="text-sm text-gray-500">
                                                                    No template
                                                                    content
                                                                    available
                                                                </div>
                                                            </div>
                                                        )}
													</>
												)}
												{/* Show Cover pages backside (After the content) */}
												{entry && showCoverPages ? (
													<LogModuleBack
														bookId={entry?.id}
													/>
												) : null}
                                            </div>
                                        </div>
                                    </TransformComponent>
                                </div>
                            </div>
                        </>
                    );
                }}
            </TransformWrapper>
        </div>
    );
};

function mapStateToProps(store: IStore) {
    const inspectorStore = inspectorSelectors._getStore(store);
    const selectedLog = inspectorSelectors.getSelectedLog(inspectorStore);
    const entry = inspectorSelectors.getEntryFilter(inspectorStore);
    const showCoverPages = inspectorSelectors.getShowCoverPages(inspectorStore);

    if (Array.isArray(selectedLog?.log)) {
        return {
            logTemplate: selectedLog?.logPrintTemplate,
            logConfig: selectedLog?.logConfig,
            logDataArray: selectedLog?.log,
            logData: selectedLog?.log[0],
            logType:
                selectedLog?.log[0]?.typeOfLogs ||
                selectedLog?.log[0]?.logType ||
                selectedLog?.log[0]?.modelName,
            entry,
            showCoverPages,
        };
    } else {
        return {
            logTemplate: selectedLog?.logPrintTemplate,
            logConfig: selectedLog?.logConfig,
            logDataArray: [],
            logData: selectedLog?.log,
            logType:
                selectedLog?.log?.typeOfLogs ||
                selectedLog?.log?.logType ||
                selectedLog?.log?.modelName,
            entry,
            showCoverPages,
        };
    }
}

const reduxConnector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(TemplateRenderer);
